// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Dices, Trophy, Users, Coins, ChevronLeft, Loader2, 
  Plus, Play, CircleDashed, Sparkles
} from 'lucide-react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { 
  getFirestore, collection, doc, setDoc, updateDoc, onSnapshot, 
  query, where, increment 
} from 'firebase/firestore';

// --- FIREBASE INITIALIZATION (SAFE FOR SANDBOX & EXTERNAL APPS) ---
const firebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : {};
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);
const db = getFirestore(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';

// --- CSS STYLES ---
const GameStyles = () => (
  <style>{`
    .scrollbar-hide::-webkit-scrollbar { display: none; }
    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
    @keyframes starScroll {
      from { transform: translateY(0); }
      to { transform: translateY(100vh); }
    }
    .text-glow-cyan { text-shadow: 0 0 10px rgba(34,211,238,0.8), 0 0 20px rgba(34,211,238,0.4); }
  `}</style>
);

export const Hyce = ({ currentUser, onClose }) => {
  const mountRef = useRef(null);
  
  // --- MATCH STATE ---
  const [activeMatchId, setActiveMatchId] = useState(null);
  const [match, setMatch] = useState(null);
  const [lobbies, setLobbies] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  // --- LOBBY CREATION STATE ---
  const [createBet, setCreateBet] = useState('100');
  const [createRounds, setCreateRounds] = useState(3); 
  const [createMaxPlayers, setCreateMaxPlayers] = useState(2);

  // --- 3D ENGINE STATE ---
  const [isRollingUI, setIsRollingUI] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [displayedRoll, setDisplayedRoll] = useState(null);

  const userBalance = currentUser?.cred || currentUser?.credits || 0;

  const engineRef = useRef({
    initialized: false,
    scene: null, camera: null, renderer: null, world: null,
    diceMeshes: [], diceBodies: [],
    animationId: null,
    isRolling: false,
    rollSettledFrames: 0,
    targetCamX: 0, targetCamZ: 14
  });

  const calculateRef = useRef();

  const uid = currentUser?.uid || currentUser?.id;

  // 1. LOAD 3D SCRIPTS (Safeguarded against StrictMode & HMR)
  useEffect(() => {
    let isMounted = true;
    
    const loadScript = (src, globalVar) => new Promise((resolve) => {
      if (window[globalVar]) {
        resolve();
        return;
      }
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', resolve);
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      document.head.appendChild(script);
    });

    Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js', 'THREE'),
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/cannon.js/0.6.2/cannon.min.js', 'CANNON')
    ]).then(() => {
      const interval = setInterval(() => {
        if (window.THREE && window.CANNON) {
          clearInterval(interval);
          if (isMounted) setScriptsLoaded(true);
        }
      }, 50);
    });

    return () => { isMounted = false; };
  }, []);

  // 2. LISTEN TO OPEN LOBBIES
  useEffect(() => {
    if (!uid || activeMatchId) return;
    
    const q = query(collection(db, 'hyce_matches'), where('status', '==', 'waiting'));
    const unsub = onSnapshot(q, (snap) => {
      const allLobbies = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLobbies(allLobbies.filter(l => l.status === 'waiting'));
    });
    return () => unsub();
  }, [uid, activeMatchId]);

  // 3. DISTRIBUTED GAME ENGINE (PAYOUTS & REFUNDS)
  const claimPayout = async (matchData) => {
    try {
        await updateDoc(doc(db, 'hyce_matches', matchData.id), { payoutClaimed: true });
        await setDoc(doc(db, 'users', uid), {
            cred: increment(matchData.pot),
            credits: increment(matchData.pot)
        }, { merge: true });
    } catch (e) { console.error("Payout error", e); }
  };

  const processCancelled = async (matchData) => {
    const me = matchData.players.find(p => p.id === uid);
    if (me && !me.refunded) {
        try {
            await setDoc(doc(db, 'users', uid), {
                cred: increment(matchData.betAmount),
                credits: increment(matchData.betAmount)
            }, { merge: true });
            const updatedPlayers = matchData.players.map(p => 
                p.id === uid ? { ...p, refunded: true } : p
            );
            await updateDoc(doc(db, 'hyce_matches', matchData.id), { players: updatedPlayers });
        } catch(e) { console.error("Refund error", e); }
    }
    setActiveMatchId(null);
    setMatch(null);
  };

  // 4. ACTIVE MATCH LISTENER
  useEffect(() => {
    if (!activeMatchId || !uid) return;
    
    const unsub = onSnapshot(doc(db, 'hyce_matches', activeMatchId), (docSnap) => {
      if (docSnap.exists()) {
        const matchData = { id: docSnap.id, ...docSnap.data() };
        setMatch(matchData);
        
        // EVERYONE PROCESSES THEIR OWN CANCELLED REFUNDS
        if (matchData.status === 'cancelled') {
            processCancelled(matchData);
            return;
        }

        // WINNER PROCESSES THEIR OWN PAYOUT
        if (matchData.status === 'finished' && matchData.winner === uid && !matchData.payoutClaimed) {
            claimPayout(matchData);
        }

        // HOST EVALUATES ROUNDS
        if (matchData.hostId === uid && matchData.status === 'playing') {
          const allRolled = matchData.players.every(p => p.hasRolled);
          if (allRolled) {
            evaluateRound(matchData);
          }
        }
      } else {
        setActiveMatchId(null);
        setMatch(null);
      }
    });
    return () => unsub();
  }, [activeMatchId, uid]);

  const handleCreateMatch = async () => {
    if (!uid) return alert("User not identified.");
    
    const bet = parseInt(createBet);
    if (isNaN(bet) || bet <= 0) return alert("Invalid bet amount.");
    if (userBalance < bet) return alert("Insufficient CRD.");

    setIsProcessing(true);
    try {
      // Safely update user balance, creating the document if it doesn't exist
      await setDoc(doc(db, 'users', uid), {
        cred: increment(-bet),
        credits: increment(-bet)
      }, { merge: true });

      const matchId = `hyce_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      const newMatch = {
        hostId: uid,
        status: 'waiting',
        maxPlayers: createMaxPlayers,
        betAmount: bet,
        totalRounds: createRounds,
        currentRound: 1,
        pot: bet,
        winner: null,
        payoutClaimed: false,
        players: [{
          id: uid,
          name: currentUser.displayName || currentUser.name || 'Unknown',
          avatar: currentUser.photoURL || currentUser.avatar || '',
          score: 0,
          roll: null,
          hasRolled: false,
          refunded: false
        }]
      };

      await setDoc(doc(db, 'hyce_matches', matchId), newMatch);
      setActiveMatchId(matchId);
    } catch (e) {
      console.error(e);
      alert("Failed to create match. Check console for details.");
    }
    setIsProcessing(false);
  };

  const handleJoinMatch = async (matchToJoin) => {
    if (!uid) return;
    if (userBalance < matchToJoin.betAmount) return alert("Insufficient CRD to join.");
    if (matchToJoin.players.length >= matchToJoin.maxPlayers) return alert("Lobby is full.");
    if (matchToJoin.players.find(p => p.id === uid)) {
      setActiveMatchId(matchToJoin.id);
      return;
    }

    setIsProcessing(true);
    try {
      // Safely update user balance
      await setDoc(doc(db, 'users', uid), {
        cred: increment(-matchToJoin.betAmount),
        credits: increment(-matchToJoin.betAmount)
      }, { merge: true });

      const newPlayer = {
        id: uid,
        name: currentUser.displayName || currentUser.name || 'Unknown',
        avatar: currentUser.photoURL || currentUser.avatar || '',
        score: 0,
        roll: null,
        hasRolled: false,
        refunded: false
      };

      await updateDoc(doc(db, 'hyce_matches', matchToJoin.id), {
        players: [...matchToJoin.players, newPlayer],
        pot: increment(matchToJoin.betAmount)
      });

      setActiveMatchId(matchToJoin.id);
    } catch (e) {
      console.error(e);
      alert("Failed to join match.");
    }
    setIsProcessing(false);
  };

  const handleStartMatch = async () => {
    if (!match || match.hostId !== uid) return;
    if (match.players.length < 2) return alert("Need at least 2 players to start.");
    
    await updateDoc(doc(db, 'hyce_matches', match.id), { status: 'playing' });
  };

  const handleLeaveLobby = async () => {
    if (!match) return;
    
    if (match.hostId === uid && match.status === 'waiting') {
        await updateDoc(doc(db, 'hyce_matches', match.id), { status: 'cancelled' });
    } 
    else if (match.status === 'waiting') {
        const updatedPlayers = match.players.filter(p => p.id !== uid);
        await updateDoc(doc(db, 'hyce_matches', match.id), {
            players: updatedPlayers,
            pot: increment(-match.betAmount)
        });
        await setDoc(doc(db, 'users', uid), { 
            cred: increment(match.betAmount), 
            credits: increment(match.betAmount) 
        }, { merge: true });
        setActiveMatchId(null);
        setMatch(null);
    }
  };

  // HOST SCORE EVALUATION (SUDDEN DEATH LOGIC)
  const evaluateRound = async (matchData) => {
    setTimeout(async () => {
      try {
        const maxRoll = Math.max(...matchData.players.map(p => p.roll || 0));
        const winnersOfRound = matchData.players.filter(p => p.roll === maxRoll);
        
        let updatedPlayers = matchData.players.map(p => ({
          ...p,
          score: p.score + (winnersOfRound.some(w => w.id === p.id) ? 1 : 0),
          roll: null, 
          hasRolled: false
        }));

        const pointsToWin = Math.ceil(matchData.totalRounds / 2);
        const gameWinners = updatedPlayers.filter(p => p.score >= pointsToWin);

        if (gameWinners.length === 1) {
          await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'hyce_matches', matchData.id), {
            players: updatedPlayers,
            status: 'finished',
            winner: gameWinners[0].id,
            payoutClaimed: false
          });

        } else if (gameWinners.length > 1) {
          // SUDDEN DEATH TIE-BREAKER
          await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'hyce_matches', matchData.id), {
            players: updatedPlayers,
            currentRound: increment(1),
            totalRounds: increment(1) 
          });
        } else {
          // NEXT ROUND
          await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'hyce_matches', matchData.id), {
            players: updatedPlayers,
            currentRound: increment(1)
          });
        }
      } catch (e) {
        console.error("Evaluation Error:", e);
      }
    }, 2500); 
  };

  // 5. 3D PHYSICS ROLL INITIATION
  const handleRollPhysics = () => {
    if (isRollingUI || !match || !uid) return;
    const me = match.players.find(p => p.id === uid);
    if (!me || me.hasRolled) return;

    setIsRollingUI(true);
    setShowResult(false);
    
    const engine = engineRef.current;
    if (!engine.world) return;

    engine.isRolling = true;
    engine.rollSettledFrames = 0;

    const { THREE } = window;
    engine.diceBodies.forEach((body, idx) => {
      body.wakeUp();
      const startX = (Math.random() - 0.5) * 6;
      const startZ = -5 + Math.random() * 2;
      body.position.set(startX, 14 + (idx * 3), startZ);
      const tempQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI));
      body.quaternion.set(tempQuat.x, tempQuat.y, tempQuat.z, tempQuat.w);
      const forceX = -startX * 0.8 + (Math.random() - 0.5) * 10;
      const forceZ = 8 + Math.random() * 5; 
      body.velocity.set(forceX, 12 + Math.random() * 8, forceZ);
      body.angularVelocity.set((Math.random() - 0.5) * 60, (Math.random() - 0.5) * 60, (Math.random() - 0.5) * 60);
    });
  };

  // 6. 3D PHYSICS RESULT CALCULATION (Connected to Firestore)
  calculateRef.current = async () => {
    if (!match || !uid) return;
    const { THREE } = window;
    const engine = engineRef.current;
    let totalResult = 0;
    
    engine.diceMeshes.forEach(mesh => {
      const localUp = new THREE.Vector3(0, 1, 0).applyQuaternion(mesh.quaternion.clone().invert());
      let maxDot = -Infinity;
      let rolledVal = 1;
      const faces = [
        { vec: new THREE.Vector3(1,0,0), val: 1 }, { vec: new THREE.Vector3(-1,0,0), val: 6 },
        { vec: new THREE.Vector3(0,1,0), val: 2 }, { vec: new THREE.Vector3(0,-1,0), val: 5 },
        { vec: new THREE.Vector3(0,0,1), val: 3 }, { vec: new THREE.Vector3(0,0,-1), val: 4 }
      ];
      faces.forEach(f => {
        const dot = localUp.dot(f.vec);
        if (dot > maxDot) { maxDot = dot; rolledVal = f.val; }
      });
      totalResult += rolledVal;
    });

    setDisplayedRoll(totalResult);
    setIsRollingUI(false);
    setShowResult(true);

    const updatedPlayers = match.players.map(p => 
      p.id === uid ? { ...p, roll: totalResult, hasRolled: true } : p
    );

    try {
      await updateDoc(doc(db, 'hyce_matches', match.id), {
        players: updatedPlayers
      });
    } catch(e) { console.error("Roll update failed:", e); }
  };

  // 7. INITIALIZE 3D ENGINE
  useEffect(() => {
    if (!scriptsLoaded || !mountRef.current || engineRef.current.initialized) return;
    
    const { THREE, CANNON } = window;
    if (!THREE || !CANNON) return; 

    const engine = engineRef.current;
    engine.initialized = true;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.set(0, 20, 15);
    camera.lookAt(0, 2, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    mountRef.current.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0x222222, 1.8);
    scene.add(ambient);
    const spot = new THREE.SpotLight(0xffffff, 2.5);
    spot.position.set(0, 35, 5);
    spot.castShadow = true;
    scene.add(spot);

    const cyanL = new THREE.PointLight(0x06b6d4, 4, 50);
    cyanL.position.set(-12, 8, -10);
    scene.add(cyanL);

    const world = new CANNON.World();
    world.gravity.set(0, -45, 0);
    
    // Procedural Environment Texture
    const createDarkFeltTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 512; canvas.height = 512;
      const ctx = canvas.getContext('2d');
      if (!ctx) return new THREE.Texture();
      ctx.fillStyle = '#040814'; 
      ctx.fillRect(0, 0, 512, 512);
      
      for (let i = 0; i < 50000; i++) {
        ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.8)';
        ctx.fillRect(Math.random() * 512, Math.random() * 512, 1.5, 1.5);
      }
      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(10, 10);
      return texture;
    };

    const defaultMat = new CANNON.Material();
    const diceMat = new CANNON.Material();
    world.addContactMaterial(new CANNON.ContactMaterial(defaultMat, diceMat, { friction: 0.2, restitution: 0.4 }));
    world.defaultMaterial = defaultMat;

    const floorGeo = new THREE.PlaneGeometry(80, 80);
    const floorMesh = new THREE.Mesh(floorGeo, new THREE.MeshStandardMaterial({ 
      map: createDarkFeltTexture(), roughness: 0.9, metalness: 0.1 
    }));
    floorMesh.rotation.x = -Math.PI / 2;
    floorMesh.receiveShadow = true;
    scene.add(floorMesh);

    const floorBody = new CANNON.Body({ mass: 0 });
    floorBody.addShape(new CANNON.Plane());
    floorBody.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI/2);
    world.addBody(floorBody);

    const buildWall = (w, h, d, px, pz) => {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshStandardMaterial({ color: 0x000000, emissive: 0x06b6d4, emissiveIntensity: 1.2 }));
      mesh.position.set(px, h / 2, pz);
      scene.add(mesh);
      const b = new CANNON.Body({ mass: 0 });
      b.addShape(new CANNON.Box(new CANNON.Vec3(w/2, 20, d/2)));
      b.position.set(px, 0, pz);
      world.addBody(b);
    };
    buildWall(24, 3, 2, 0, -8); buildWall(24, 3, 2, 0, 7); 
    buildWall(2, 3, 14, 11, -0.5); buildWall(2, 3, 14, -11, -0.5);

    const drawRoundRect = (ctx, x, y, width, height, radius) => {
      ctx.beginPath(); ctx.moveTo(x + radius, y); ctx.lineTo(x + width - radius, y); ctx.quadraticCurveTo(x + width, y, x + width, y + radius); ctx.lineTo(x + width, y + height - radius); ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height); ctx.lineTo(x + radius, y + height); ctx.quadraticCurveTo(x, y + height, x, y + height - radius); ctx.lineTo(x, y + radius); ctx.quadraticCurveTo(x, y, x + radius, y); ctx.closePath(); ctx.fill(); ctx.stroke();
    };

    const createNeonDiceTexture = (value) => {
      const canvas = document.createElement('canvas');
      canvas.width = 256; canvas.height = 256;
      const ctx = canvas.getContext('2d');
      if (!ctx) return new THREE.Texture();
      ctx.fillStyle = '#050505'; ctx.fillRect(0, 0, 256, 256);
      ctx.fillStyle = '#0a0a0a'; ctx.strokeStyle = '#111e33'; ctx.lineWidth = 6;
      drawRoundRect(ctx, 12, 12, 232, 232, 30);
      ctx.fillStyle = '#67e8f9'; ctx.shadowColor = '#06b6d4'; ctx.shadowBlur = 35;
      const drawDot = (x, y) => { ctx.beginPath(); ctx.arc(x, y, 22, 0, Math.PI * 2); ctx.fill(); };
      const c = 128, o = 60, w = 256;
      if (value === 1) { ctx.fillStyle = '#f472b6'; ctx.shadowColor = '#d946ef'; drawDot(c, c); } 
      else if (value === 2) { drawDot(o, o); drawDot(w - o, w - o); }
      else if (value === 3) { drawDot(o, o); drawDot(c, c); drawDot(w - o, w - o); }
      else if (value === 4) { drawDot(o, o); drawDot(w - o, o); drawDot(o, w - o); drawDot(w - o, w - o); }
      else if (value === 5) { drawDot(o, o); drawDot(w - o, o); drawDot(c, c); drawDot(o, w - o); drawDot(w - o, w - o); }
      else if (value === 6) { drawDot(o, o); drawDot(o, c); drawDot(o, w - o); drawDot(w - o, o); drawDot(w - o, c); drawDot(w - o, w - o); }
      const texture = new THREE.CanvasTexture(canvas);
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      return new THREE.MeshStandardMaterial({ map: texture, roughness: 0.1, metalness: 0.5 });
    };

    const mats = [1, 6, 2, 5, 3, 4].map(v => createNeonDiceTexture(v));
    const diceGeo = new THREE.BoxGeometry(1.2, 1.2, 1.2);
    
    for (let i = 0; i < 2; i++) {
      const mesh = new THREE.Mesh(diceGeo, mats);
      mesh.castShadow = true; mesh.receiveShadow = true;
      scene.add(mesh); engine.diceMeshes.push(mesh);
      const b = new CANNON.Body({ mass: 1, linearDamping: 0.3, angularDamping: 0.3 });
      b.addShape(new CANNON.Box(new CANNON.Vec3(0.6, 0.6, 0.6)));
      b.position.set(i*2-1, 2, -3); world.addBody(b); engine.diceBodies.push(b);
    }

    const animate = () => {
      engine.animationId = requestAnimationFrame(animate);
      if (engine.world) {
        engine.world.step(1/60);
        let stopped = true;
        engine.diceBodies.forEach((b, i) => {
          engine.diceMeshes[i].position.copy(b.position);
          engine.diceMeshes[i].quaternion.copy(b.quaternion);
          if (b.velocity.lengthSquared() > 0.05) stopped = false;
        });
        if (engine.isRolling && stopped) {
          engine.rollSettledFrames++;
          if (engine.rollSettledFrames > 20) { 
              engine.isRolling = false; 
              if (calculateRef.current) calculateRef.current(); 
          }
        }
      }
      if (engine.camera && engine.scene && engine.renderer) {
        engine.camera.lookAt(0, 2, 0);
        engine.renderer.render(engine.scene, engine.camera);
      }
    };
    animate();
    engine.renderer = renderer; engine.scene = scene; engine.camera = camera; engine.world = world;

    const handleResize = () => {
      if (engine.camera && engine.renderer) {
        engine.camera.aspect = window.innerWidth / window.innerHeight;
        engine.camera.updateProjectionMatrix();
        engine.renderer.setSize(window.innerWidth, window.innerHeight);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (engine.animationId) cancelAnimationFrame(engine.animationId);
      if (mountRef.current && engine.renderer?.domElement) mountRef.current.removeChild(engine.renderer.domElement);
    };
  }, [scriptsLoaded]);


  // --- UI RENDERING ---
  const renderLobby = () => (
    <div className="flex flex-col h-full overflow-y-auto p-4 sm:p-6 lg:p-10 space-y-8 lg:space-y-12 w-full max-w-6xl mx-auto relative z-20 pointer-events-auto">
      <div className="flex justify-between items-end mb-4">
          <div>
            <h1 className="text-4xl sm:text-6xl font-black italic tracking-tighter text-glow-cyan leading-none uppercase">HYCE</h1>
            <p className="text-zinc-500 uppercase tracking-[0.2em] sm:tracking-[0.5em] text-[8px] sm:text-[10px] mt-2">Multiplayer Casino Suite</p>
          </div>
          <div className="bg-gradient-to-r from-cyan-950/40 to-black border border-cyan-800/50 p-2 sm:p-4 min-w-[100px] sm:min-w-[140px] text-right">
            <span className="text-[8px] sm:text-[10px] text-zinc-400 uppercase tracking-widest">Your CRD</span>
            <div className="text-xl sm:text-3xl font-black text-cyan-400 tabular-nums">{userBalance}</div>
          </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
        <div className="lg:col-span-1 bg-gradient-to-b from-black/90 to-black border border-cyan-900/40 p-6 lg:p-8 backdrop-blur-xl">
          <h3 className="text-lg lg:text-xl font-black italic uppercase tracking-widest text-white mb-6 flex items-center gap-3"><Plus size={18} className="text-cyan-400"/> Initialize Arena</h3>
          <div className="space-y-6">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase block mb-2 font-bold tracking-widest">Match Stake (CRD)</label>
              <input type="number" value={createBet} onChange={e => setCreateBet(e.target.value)} className="w-full bg-black/50 border border-zinc-800 p-3 sm:p-4 text-white font-black text-xl sm:text-2xl outline-none focus:border-cyan-400 transition-all" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                  <label className="text-[10px] text-zinc-500 uppercase block mb-2 font-bold tracking-widest">Rounds</label>
                  <div className="flex gap-1">
                    {[3, 5].map(v => <button key={v} onClick={() => setCreateRounds(v)} className={`flex-1 py-2 sm:py-3 border font-black text-[10px] sm:text-xs transition-all ${createRounds === v ? 'bg-cyan-500/20 text-cyan-400 border-cyan-400' : 'bg-black text-zinc-500 border-zinc-800 hover:border-zinc-600'}`}>{v}</button>)}
                  </div>
              </div>
              <div>
                  <label className="text-[10px] text-zinc-500 uppercase block mb-2 font-bold tracking-widest">Size</label>
                  <div className="flex gap-1">
                    {[2, 4].map(v => <button key={v} onClick={() => setCreateMaxPlayers(v)} className={`flex-1 py-2 sm:py-3 border font-black text-[10px] sm:text-xs transition-all ${createMaxPlayers === v ? 'bg-cyan-500/20 text-cyan-400 border-cyan-400' : 'bg-black text-zinc-500 border-zinc-800 hover:border-zinc-600'}`}>{v}</button>)}
                  </div>
              </div>
            </div>
            <button onClick={handleCreateMatch} disabled={isProcessing || !currentUser} className="w-full py-4 sm:py-6 bg-white text-black font-black uppercase tracking-widest text-xs hover:bg-cyan-400 transition-all disabled:opacity-50">Create Match</button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h3 className="text-lg lg:text-xl font-black italic uppercase tracking-widest text-white mb-6 flex items-center gap-3"><Users size={18} className="text-cyan-400" /> Available Contenders</h3>
          {lobbies.length === 0 ? (
            <div className="border border-zinc-800 border-dashed bg-black/40 backdrop-blur-md p-8 lg:p-12 text-center flex flex-col items-center justify-center">
              <CircleDashed size={40} className="text-zinc-800 mb-4" />
              <p className="text-zinc-600 font-mono text-xs uppercase tracking-widest">No active arenas. Create one to begin.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {lobbies.map(l => (
                <div key={l.id} className="bg-black/80 backdrop-blur-md border border-zinc-800 p-5 sm:p-6 group hover:border-cyan-400 transition-all flex flex-col justify-between h-full">
                  <div className="flex justify-between items-start mb-6">
                      <div>
                        <div className="text-xl sm:text-2xl font-black text-white italic tracking-tighter">{l.betAmount} CRD</div>
                        <div className="text-[9px] text-zinc-500 uppercase mt-1 tracking-widest font-bold">Best of {l.totalRounds}</div>
                      </div>
                      <div className="flex items-center gap-1 bg-zinc-900 px-3 py-1 rounded-full border border-zinc-800">
                        <Users size={12} className="text-zinc-400" />
                        <span className="text-white font-mono text-[10px]">{l.players.length}/{l.maxPlayers}</span>
                      </div>
                  </div>
                  <button onClick={() => handleJoinMatch(l)} disabled={isProcessing || !currentUser} className="w-full py-3 sm:py-4 bg-zinc-900 group-hover:bg-cyan-400 group-hover:text-black text-white font-black uppercase text-[10px] tracking-widest transition-all disabled:opacity-50">Join Arena</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderActiveGame = () => {
    if (!match) return null;
    const me = match.players.find(p => p.id === uid);
    const isHost = match.hostId === uid;

    return (
      <div className="flex flex-col h-full relative w-full overflow-hidden pointer-events-none">
        
        <div className="p-4 sm:p-6 lg:p-8 border-b border-cyan-900/30 flex items-center justify-between shrink-0 bg-gradient-to-r from-zinc-950/80 to-black backdrop-blur-md pointer-events-auto z-30">
          <div className="flex items-center gap-3 sm:gap-4">
            {match.status === 'waiting' && <button onClick={handleLeaveLobby} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-black border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors rounded-full shrink-0"><ChevronLeft size={20}/></button>}
            <div>
              <h2 className="text-lg sm:text-2xl font-black text-white uppercase italic tracking-tighter flex items-center gap-2">
                <Coins className="text-cyan-400" size={20} /> POT: {match.pot} CRD
              </h2>
              <p className="text-zinc-400 font-mono text-[8px] sm:text-[10px] uppercase tracking-widest mt-1">
                Round {match.currentRound} • Best of {match.totalRounds}
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[8px] sm:text-[10px] font-black uppercase tracking-widest border-2 ${match.status === 'waiting' ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50' : match.status === 'playing' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50' : 'bg-white text-black border-white'}`}>
              {match.status}
            </span>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-6 relative z-20">
          
          {match.status === 'waiting' && (
            <div className="text-center animate-in zoom-in bg-black/60 backdrop-blur-lg border border-zinc-800 p-8 sm:p-12 w-full max-w-md pointer-events-auto">
              <Dices size={60} strokeWidth={1.5} className="text-cyan-400 mx-auto mb-6 drop-shadow-[0_0_15px_rgba(34,211,238,0.5)]" />
              <h3 className="text-2xl sm:text-4xl font-black text-white uppercase italic tracking-tighter mb-2">Waiting for Contenders</h3>
              <p className="text-zinc-400 font-mono text-[10px] sm:text-xs uppercase tracking-widest mb-8">{match.players.length} of {match.maxPlayers} Joined</p>
              
              {isHost && (
                <button onClick={handleStartMatch} disabled={match.players.length < 2} className="w-full py-4 sm:py-6 bg-cyan-400 text-black font-black uppercase tracking-[0.2em] text-xs sm:text-sm hover:bg-white transition-all shadow-[0_0_20px_rgba(34,211,238,0.3)] disabled:opacity-30 flex items-center justify-center gap-2">
                  <Play size={16} fill="currentColor" /> Begin Match
                </button>
              )}
            </div>
          )}

          {match.status === 'finished' && (
            <div className="text-center animate-in zoom-in slide-in-from-bottom-10 flex flex-col items-center justify-center w-full max-w-md bg-black/80 backdrop-blur-2xl border-2 border-cyan-500 p-8 sm:p-12 pointer-events-auto">
              <Trophy size={80} strokeWidth={1.5} className="text-cyan-400 mb-6 drop-shadow-[0_0_30px_rgba(34,211,238,0.6)]" />
              <h3 className="text-4xl sm:text-6xl font-black text-white uppercase italic tracking-tighter mb-2">
                {match.winner === uid ? 'Victory' : 'Defeat'}
              </h3>
              <p className="text-zinc-400 font-bold uppercase tracking-widest text-[10px] sm:text-xs mb-8">
                {match.winner === uid ? `You claimed ${match.pot} CRD` : 'Your credits were lost to the void'}
              </p>
              <button onClick={() => { setActiveMatchId(null); setMatch(null); }} className="w-full py-4 sm:py-5 border-2 border-white text-white font-black uppercase tracking-[0.2em] text-[10px] sm:text-xs hover:bg-white hover:text-black transition-colors">
                Return to Lobby
              </button>
            </div>
          )}

          {showResult && match.status === 'playing' && (
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center pointer-events-none animate-in zoom-in slide-in-from-bottom-8">
                <h1 className="text-[8rem] sm:text-[12rem] font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-cyan-500 drop-shadow-[0_0_40px_rgba(34,211,238,0.5)] leading-none">
                  {displayedRoll}
                </h1>
             </div>
          )}
        </div>

        <div className="bg-gradient-to-t from-zinc-950/90 to-transparent pt-20 shrink-0 flex flex-col gap-4 pointer-events-auto z-30">
            
            {match.status === 'playing' && (
              <div className="w-full max-w-sm mx-auto px-4">
                 {me?.hasRolled ? (
                   <div className="w-full py-6 sm:py-8 border border-zinc-800 bg-black/80 backdrop-blur-md text-zinc-500 font-black uppercase tracking-widest text-center italic text-[10px] sm:text-xs flex items-center justify-center gap-2">
                      <Loader2 className="animate-spin" size={14}/> Waiting on others...
                   </div>
                 ) : (
                   <button onClick={handleRollPhysics} disabled={isRollingUI} className="w-full py-6 sm:py-8 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-black uppercase tracking-[0.3em] text-xl sm:text-2xl transition-all shadow-[0_0_40px_rgba(34,211,238,0.4)] hover:shadow-[0_0_60px_rgba(34,211,238,0.6)] flex flex-col items-center justify-center gap-1">
                      <span>Throw Dice</span>
                      {isRollingUI && <span className="text-[10px] font-mono tracking-widest opacity-70 animate-pulse">Physics Active...</span>}
                   </button>
                 )}
              </div>
            )}

            <div className="bg-black/80 backdrop-blur-md border-t border-zinc-800 p-4 sm:p-6 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              {match.players.map(p => (
                <div key={p.id} className={`p-3 sm:p-4 border flex flex-col gap-3 transition-colors ${p.id === match.winner ? 'border-cyan-400 bg-cyan-400/10 shadow-[0_0_20px_rgba(34,211,238,0.2)]' : p.id === uid ? 'border-zinc-600 bg-zinc-900/50' : 'border-zinc-800 bg-black/50'}`}>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-black border border-zinc-700 overflow-hidden shrink-0 flex items-center justify-center text-zinc-500 font-black uppercase text-xs sm:text-sm">
                      {p.avatar ? <img src={p.avatar} className="w-full h-full object-cover grayscale" /> : p.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`font-black text-[9px] sm:text-[10px] uppercase tracking-widest truncate ${p.id === uid ? 'text-cyan-400' : 'text-white'}`}>
                        {p.id === uid ? 'YOU' : p.name}
                      </p>
                      <p className="text-zinc-500 font-mono text-[8px] sm:text-[9px] uppercase tracking-widest mt-1">Score: <span className="text-white">{p.score}</span></p>
                    </div>
                  </div>
                  
                  <div className={`mt-1 py-1.5 sm:py-2 text-center flex items-center justify-center gap-2 text-[9px] sm:text-[10px] font-black uppercase tracking-widest border ${p.hasRolled ? 'bg-cyan-950/30 text-cyan-400 border-cyan-900/50' : 'bg-black text-zinc-600 border-zinc-800'}`}>
                    {p.hasRolled ? (
                        <span className="flex items-center gap-2">
                           <Dices size={14} className="text-cyan-500" /> Rolled {p.roll}
                        </span>
                    ) : 'Waiting'}
                  </div>
                </div>
              ))}
              {Array.from({ length: match.maxPlayers - match.players.length }).map((_, i) => (
                <div key={`empty-${i}`} className="p-3 sm:p-4 border border-dashed border-zinc-800 bg-black/40 flex items-center justify-center opacity-50">
                  <span className="text-zinc-600 font-mono text-[8px] sm:text-[10px] uppercase tracking-widest">Empty Slot</span>
                </div>
              ))}
            </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[400] bg-black flex items-center justify-center animate-in fade-in duration-200">
      <GameStyles />

      <div className="w-full h-full bg-black flex flex-col overflow-hidden relative">
        
        {/* 3D Canvas Background Layer */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
            <div ref={mountRef} className="absolute inset-0" />
            <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(rgba(34,211,238,1)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,1)_1px,transparent_1px)] bg-[size:50px_50px]" />
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(white_1px,transparent_1px)] bg-[size:80px_80px]" style={{ animation: 'starScroll 20s linear infinite' }} />
            <div className="absolute inset-0 shadow-[inset_0_0_150px_rgba(0,0,0,1)]" />
        </div>

        {!activeMatchId && (
          <div className="p-6 lg:p-8 border-b border-zinc-800 flex justify-between items-center bg-zinc-950/80 backdrop-blur-md shrink-0 sticky top-0 z-20 pointer-events-auto">
            <div>
              <h2 className="text-4xl lg:text-5xl font-black text-white uppercase italic tracking-tighter flex items-center gap-4">
                <Dices className="text-cyan-500" size={40} /> HYCE
              </h2>
              <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em] mt-2">High-Stakes Dice Arena</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="hidden lg:block text-right border-r border-zinc-800 pr-6">
                <p className="text-zinc-500 font-mono text-[9px] uppercase tracking-widest mb-1">Your Wallet</p>
                <p className="text-white font-black text-lg italic tracking-tighter">{userBalance} CRD</p>
              </div>
              <button onClick={onClose} className="w-12 h-12 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors rounded-full shrink-0 shadow-xl">
                <X size={20} strokeWidth={2.5}/>
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden relative z-10 pointer-events-none">
          {activeMatchId ? renderActiveGame() : renderLobby()}
        </div>
      </div>
    </div>
  );
};

// --- SANDBOX PREVIEW WRAPPER ---
export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({ cred: 1000 });
  const auth = getAuth();
  
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Auth failed:", err);
      }
    };
    initAuth();
    
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        const userRef = doc(getFirestore(), 'artifacts', typeof __app_id !== 'undefined' ? __app_id : 'default-app-id', 'users', u.uid);
        onSnapshot(userRef, (snap) => {
          if (snap.exists()) {
            setProfile(snap.data());
          } else {
            setDoc(userRef, { cred: 1000, name: u.displayName || 'Player', uid: u.uid });
          }
        });
      }
    });
    return () => unsubscribe();
  }, []);

  if (!user) {
    return <div className="min-h-screen bg-black flex flex-col gap-4 items-center justify-center text-cyan-400 font-black tracking-widest"><Loader2 className="animate-spin" size={40}/> Loading Sandbox</div>;
  }

  const mergedUser = { ...user, ...profile };

  return <Hyce currentUser={mergedUser} onClose={() => console.log('Close clicked')} />;
}